/**
 * DiscourseRenderer — 포럼형 (Mobile 기본)
 */
import React from 'react';
import { Trans } from '@lingui/macro';
import { Post } from '../../../models/Board';
import { cn } from '../../../lib/utils';
import {
  Eye, Heart, MessageCircle, Pin, Megaphone,
} from 'lucide-react';
import {
  StyleRendererProps, timeAgo,
  ImportanceBadge, QnaStatusBadge, splitPinnedPosts, CommentBadge, extractFirstImageUrl, NotifDot,
} from './shared';
import { UserAvatar } from '../../components/UserAvatar';

export const DiscourseRenderer: React.FC<StyleRendererProps> = ({
  posts, onPostClick, selectedPostId, boardType, unreadPostIds, notifiedPostIds,
}) => {
  const isNotice = boardType === 'notice';
  const isQna = boardType === 'qna';
  const { pinned, normal } = isNotice ? splitPinnedPosts(posts) : { pinned: [], normal: posts };

  return (
  <div className="h-full overflow-y-auto px-2 lg:px-5">
    {/* ── 고정글 영역 (notice 전용) ── */}
    {isNotice && pinned.length > 0 && (
      <div className="mb-2">
        <div className="flex items-center gap-2 px-4 pt-3 pb-1.5">
          <Pin className="size-3 text-primary" strokeWidth={2.5} />
          <span className="text-meta-sm font-semibold text-primary tracking-wide"><Trans>Pinned notices</Trans></span>
          <div className="flex-1 h-px bg-primary/15" />
        </div>
        <div className="divide-y divide-border/50">
          {pinned.map(post => (
            <DiscoursePostItem key={post.id} post={post} onPostClick={onPostClick} selectedPostId={selectedPostId} isNotice isQna={false} unreadPostIds={unreadPostIds} notifiedPostIds={notifiedPostIds} />
          ))}
        </div>
        {normal.length > 0 && <div className="h-px bg-border mx-4 mt-2" />}
      </div>
    )}
    <div className="divide-y divide-border/50">
      {normal.map(post => (
        <DiscoursePostItem key={post.id} post={post} onPostClick={onPostClick} selectedPostId={selectedPostId} isNotice={isNotice} isQna={isQna} unreadPostIds={unreadPostIds} notifiedPostIds={notifiedPostIds} />
      ))}
    </div>
  </div>
  );
};

/** Discourse 포스트 아이템 서브컴포넌트.
 *  본문에 이미지 있으면 좌측에 썸네일(80x80) 표시, 없으면 기존처럼 아바타 표시. */
const DiscoursePostItem: React.FC<{
  post: Post; onPostClick: (post: Post) => void;
  selectedPostId?: string | null; isNotice?: boolean; isQna?: boolean; unreadPostIds?: Set<string>; notifiedPostIds?: Set<string>;
}> = ({ post, onPostClick, selectedPostId, isNotice, isQna, unreadPostIds, notifiedPostIds }) => {
  const thumb = extractFirstImageUrl(post.content);
  return (
  <button
    onClick={() => onPostClick(post)}
    className={cn(
      'w-full text-left flex items-start gap-3 px-4 py-3.5 rounded-lg',
      selectedPostId === post.id
        ? 'bg-accent'
        : 'hover:bg-muted/40 active:bg-muted/60',
      'transition-colors duration-150 cursor-pointer',
      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset',
      post.is_pinned && selectedPostId !== post.id && 'bg-primary/[0.02]',
    )}
  >
    {/* 좌측: 썸네일(본문 첫 이미지) 있으면 80x80, 없으면 아바타 */}
    {thumb ? (
      <div className="w-20 h-20 shrink-0 rounded-md overflow-hidden bg-muted">
        <img
          src={thumb}
          alt=""
          className="w-full h-full object-cover"
          loading="lazy"
          onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
        />
      </div>
    ) : (
      <UserAvatar src={post.author.photoUrl ?? null} name={post.author.name} className="size-10 shrink-0" />
    )}

    {/* 콘텐츠 */}
    <div className="flex-1 min-w-0">
      {/* 뱃지 + 제목 */}
      <div className="flex items-center gap-1.5 mb-1">
        {isQna && <QnaStatusBadge post={post} />}
        {isNotice && <ImportanceBadge post={post} />}
        {post.is_pinned && (
          <Pin className="size-3 text-primary shrink-0" strokeWidth={2.5} />
        )}
        {post.is_announcement && (
          <Megaphone className="size-3 text-warning shrink-0" strokeWidth={2} />
        )}
        <h3 className={cn(
          'text-body leading-snug truncate',
          post.is_pinned ? 'font-semibold text-foreground' : 'font-medium text-foreground/90',
        )}>
          {post.title}
        </h3>
        {notifiedPostIds?.has(post.id) && <NotifDot />}
        {/* 채택 답변 있으면 뱃지 */}
        {isQna && post.metadata?.accepted_comment_id && (
          <span className="shrink-0 text-meta-sm px-1.5 py-0.5 rounded bg-badge-success-bg text-badge-success font-semibold">
            <Trans>Accepted</Trans> ✓
          </span>
        )}
      </div>

      {/* 본문 미리보기 */}
      {post.plain_text && (
        <p className="text-meta text-muted-foreground line-clamp-2 leading-relaxed mb-2">
          {post.plain_text}
        </p>
      )}

      {/* 메타 정보 */}
      <div className="flex items-center gap-2 text-meta-sm text-muted-foreground">
        <span className="font-medium text-foreground/70">{post.author.name}</span>
        <span className="opacity-40">·</span>
        <span>{timeAgo(post.created_at)}</span>

        {/* 통계 (우측 정렬) */}
        <div className="ml-auto flex items-center gap-3">
          {post.comment_count > 0 && (
            <CommentBadge count={post.comment_count} isUnread={unreadPostIds?.has(post.id)} variant="icon" />
          )}
          {post.view_count > 0 && (
            <span className="flex items-center gap-0.5">
              <Eye className="size-3 opacity-50" />
              <span className="tabular-nums">{post.view_count}</span>
            </span>
          )}
          {post.like_count > 0 && (
            <span className="flex items-center gap-0.5">
              <Heart className="size-3 opacity-50" />
              <span className="tabular-nums">{post.like_count}</span>
            </span>
          )}
        </div>
      </div>

      {/* 태그 */}
      {post.tags && post.tags.length > 0 && (
        <div className="flex gap-1 mt-2 flex-wrap">
          {post.tags.slice(0, 4).map(tag => (
            <span
              key={tag}
              className="text-meta-sm px-2 py-0.5 rounded-full bg-primary/8 text-primary/80 font-medium"
            >
              #{tag}
            </span>
          ))}
          {post.tags.length > 4 && (
            <span className="text-meta-sm text-muted-foreground">+{post.tags.length - 4}</span>
          )}
        </div>
      )}
    </div>
  </button>
  );
};
